import React, { useState, useMemo, useCallback } from 'react';
import { MessageItemProps } from '../types';
import { ScreenReaderUtils } from '../utils/accessibility';
import { renderMarkdown } from '../utils/markdownRenderer';
import ThinkingSection from './ThinkingSection';
import ReferenceList from './ReferenceList';
import Avatar from './Avatar';
import './MessageItem.css';
import '../styles/markdown.css';

const MessageItem: React.FC<MessageItemProps> = React.memo(({
  message,
  isOwn,
  showTimestamp,
  onReferenceClick
}) => {
  const [showFullTimestamp, setShowFullTimestamp] = useState(false);
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(false);

  // Memoize expensive timestamp calculations
  const timestampData = useMemo(() => {
    const now = new Date();
    const messageDate = new Date(message.timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageDate.getTime()) / (1000 * 60));
    
    let shortFormat: string;
    if (diffInMinutes < 1) {
      shortFormat = 'Just now';
    } else if (diffInMinutes < 60) {
      shortFormat = `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      shortFormat = `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      if (days === 1) {
        shortFormat = '1 day ago';
      } else if (days < 7) {
        shortFormat = `${days} days ago`;
      } else {
        shortFormat = messageDate.toLocaleDateString();
      }
    }

    return {
      short: shortFormat,
      full: messageDate.toLocaleString(),
      iso: message.timestamp.toISOString()
    };
  }, [message.timestamp]);

  // Memoize message identifiers
  const messageIds = useMemo(() => ({
    messageId: `message-${message.id}`,
    timestampId: `timestamp-${message.id}`
  }), [message.id]);

  // Memoize screen reader content
  const screenReaderContent = useMemo(() => ({
    timestamp: ScreenReaderUtils.formatTimestampForSR(message.timestamp),
    message: ScreenReaderUtils.formatMessageForSR(message.sender, message.content, message.timestamp)
  }), [message.sender, message.content, message.timestamp]);

  // Memoize thinking toggle callback
  const handleThinkingToggle = useCallback(() => {
    setIsThinkingExpanded(!isThinkingExpanded);
  }, [isThinkingExpanded]);

  // Memoize timestamp hover handlers
  const timestampHandlers = useMemo(() => ({
    onMouseEnter: () => setShowFullTimestamp(true),
    onMouseLeave: () => setShowFullTimestamp(false),
    onFocus: () => setShowFullTimestamp(true),
    onBlur: () => setShowFullTimestamp(false)
  }), []);

  // Get avatar configuration based on sender
  const getAvatarConfig = () => {
    if (isOwn) {
      return {
        alt: 'You',
        className: 'avatar--user',
        fallbackText: 'U'
      };
    } else {
      // Assistant avatar - use message avatar data if available
      const messageAvatar = message.avatar;
      return {
        alt: messageAvatar?.alt || message.sender,
        className: 'avatar--assistant',
        fallbackText: messageAvatar?.fallbackText || 'AI',
        isOnline: true,
        src: messageAvatar?.src // Use avatar image if provided
      };
    }
  };

  const avatarConfig = getAvatarConfig();

  const isInitialMessage = message.metadata?.isInitialMessage;
  // Don't apply thinking class to entire message - thinking section handles its own styling
  
  return (
    <article 
      className={`message-item message-accessible ${isOwn ? 'message-item--own' : 'message-item--other'} ${isInitialMessage ? 'message-item--initial' : ''}`}
      id={messageIds.messageId}
      role="article"
      aria-labelledby={`${messageIds.messageId}-content`}
      aria-describedby={showTimestamp ? messageIds.timestampId : undefined}
      data-sender={message.sender}
    >
      {/* Screen reader only message summary */}
      <div className="sr-only" aria-live="off">
        {screenReaderContent.message}
      </div>
      
      <div className="message-item__wrapper">
        {/* Avatar - only show for non-own messages (assistant) */}
        {!isOwn && (
          <Avatar
            {...avatarConfig}
            size="medium"
            className={`message-avatar ${avatarConfig.className}`}
          />
        )}
        
        <div className="message-item__content">
        {!isOwn && (
          <div 
            className="message-item__sender"
            role="heading"
            aria-level={3}
            id={`${messageIds.messageId}-sender`}
          >
            {message.sender}
          </div>
        )}
        {/* Thinking section - show if message has thinking content */}
        {message.thinking && (
          <ThinkingSection
            content={message.thinking}
            isExpanded={isThinkingExpanded}
            onToggle={handleThinkingToggle}
          />
        )}

        {/* Main message content - only show if there's actual content */}
        {message.content && (
          <div 
            className="message-item__text"
            id={`${messageIds.messageId}-content`}
            role="text"
            aria-label={`Message content: ${message.content}`}
          >
            {message.type === 'markdown' ? 
              renderMarkdown(message.content, isInitialMessage) : 
              message.content
            }
            {/* Show streaming indicator if message is still streaming */}
            {message.isStreaming && (
              <span className="message-item__streaming-indicator" aria-live="polite">
                <span className="streaming-dots">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </span>
              </span>
            )}
          </div>
        )}

        <ReferenceList
          references={message.refs || []}
          onReferenceClick={onReferenceClick}
          messageId={messageIds.messageId}
        />
        </div>
      </div>
      {showTimestamp && (
        <time 
          className="message-item__timestamp"
          id={messageIds.timestampId}
          dateTime={timestampData.iso}
          {...timestampHandlers}
          title={timestampData.full}
          aria-label={`Message sent ${screenReaderContent.timestamp}`}
          tabIndex={0}
        >
          {showFullTimestamp ? timestampData.full : timestampData.short}
        </time>
      )}
    </article>
  );
});

export default MessageItem;